import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchRocketFamilies, fetchRockets } from "../data/rocketFamilies.js";
import {
  changeTimeToLocal,
  formatTime,
  getPhotoLocation,
  makeRecoveryString,
  parseUtcMs,
  toTitleCase,
} from "../utils.js";
import { fetchHomeInfo } from "../data/home.js";
import { fetchNewLaunchInfo } from "../data/launches.js";
import DarkBarChart from "../graphComponents/BarChart.js";
import StatCard from "../informationCards/StatCard.js";
import { fetchPads } from "../data/pads.js";
import { fetchZones } from "../data/recoveryZones.js";
import { fetchStageInfo, fetchStages } from "../data/stages.js";
import { fetchSpacecraft } from "../data/spacecraft.js";
import { getCalendarInformation } from "../data/stats.js";
import {
  buildReuseChart,
  buildTimelineRowFromFlights,
  extractFlightDates,
  reuseViewLabels,
  statusIsActive,
} from "../graphComponents/reuseTimeline.js";

// Re-export for any legacy imports of buildReuseChart from Home
export { buildReuseChart } from "../graphComponents/reuseTimeline.js";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const launchPalette = ["#238bb8", "#2563b8", "#8067d8", "#168f7d", "#596173"];

export const getLaunchChartColor = (rocket, fallbackIndex = 0) => {
  const name = String(rocket?.name || "").toLowerCase();

  if (name.includes("starship") || name.includes("super heavy")) {
    return "#8067d8";
  }

  if (name.includes("falcon heavy")) {
    return "#2563b8";
  }

  if (name.includes("falcon 1")) {
    return "#596173";
  }

  if (name.includes("falcon")) {
    return "#238bb8";
  }

  if (name.includes("dragon")) {
    return "#168f7d";
  }

  return launchPalette[fallbackIndex % launchPalette.length];
};

const addSyntheticReuseData = (rows) =>
  rows.map((row, rowIndex) => {
    const start = Date.UTC(2020 + Math.min(rowIndex, 4), rowIndex % 12, 1);
    const spanDays = Math.max(1, row.dots[row.dots.length - 1] || 1) * 18;
    const flightDays = row.dots.map((dot) => Math.round((dot / Math.max(1, row.dots[row.dots.length - 1])) * spanDays));

    return {
      ...row,
      flightDays,
      flightDates: flightDays.map((day) => new Date(start + day * 86400000).toISOString()),
    };
  });

const buildCalendarFilter = (families) => ({
  rocket__family: families.reduce((acc, family) => {
    acc[family.id] = { hide__rocket: Object.fromEntries(family.rockets.map((rocket) => [rocket.id, true])) };
    return acc;
  }, {}),
});

const getElapsedLabel = (time) => {
  const diffMs = Date.now() - parseUtcMs(time);

  if (Number.isNaN(diffMs) || diffMs < 0) {
    return "Upcoming";
  }

  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(hours / 24);

  if (days >= 1) {
    return `${days}d ago`;
  }

  return `${hours || 1}h ago`;
};

const getClockParts = (time, now) => {
  const target = parseUtcMs(time);
  const diffMs = target - now;
  const totalSeconds = Math.max(0, Math.floor(Math.abs(diffMs) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    prefix: diffMs >= 0 ? "T-" : "T+",
    days,
    hours,
    minutes,
    seconds,
  };
};

const getShortDate = (time) => {
  const ms = parseUtcMs(time);

  if (Number.isNaN(ms)) {
    return "Date TBD";
  }

  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const STATIC_MEDIA_HOST = "https://boostertracker-static.s3.amazonaws.com";
const STARSHIP_FALLBACK_IMAGE = "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg";
const FALCON_FALLBACK_IMAGE = "/media/rocket_pad_photos/Falcon_LC39A_launches_photo.jpg";

const launchPhotoFallbacksByPad = {
  "LC-39A": "/media/rocket_pad_photos/Falcon_LC39A_launches_photo.jpg",
  "SLC-40": "/media/rocket_pad_photos/Falcon_SLC40_launches_photo.jpg",
  "SLC-4E": "/media/rocket_pad_photos/Falcon_SLC4E_launches_photo.jpg",
  "OLP-1": STARSHIP_FALLBACK_IMAGE,
  "OLP-2": STARSHIP_FALLBACK_IMAGE,
  "OLP-A": STARSHIP_FALLBACK_IMAGE,
};

const cleanHomeImagePath = (image) => {
  if (!image) {
    return "";
  }

  return String(image).replace(/^http:\/\/127\.0\.0\.1:8000\/?/, "/");
};

const isFalconPadFallback = (image) => /\/media\/rocket_pad_photos\/Falcon_/i.test(String(image || ""));

const resolveHomePhotoUrl = (image) => {
  const imagePath = cleanHomeImagePath(image);

  if (!imagePath) {
    return "";
  }

  // Already a usable absolute URL (S3, CDN, etc.).
  if (/^https?:\/\//.test(imagePath)) {
    return imagePath;
  }

  const mediaPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;

  // Relative /media paths 404 on the SPA host in production — serve from S3.
  if (mediaPath.startsWith("/media/")) {
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      return `${getPhotoLocation()}${mediaPath}`;
    }
    return `${STATIC_MEDIA_HOST}${mediaPath}`;
  }

  return `${getPhotoLocation()}${mediaPath}`;
};

const getLaunchPhotoUrl = (launch, pads, rockets = []) => {
  const pad = pads.find((item) => item.id === launch?.pad);
  const rocket = rockets.find((item) => item.id === launch?.rocket);
  const recoveryImage = (launch?.stage_and_recoveries || [])
    .map((recovery) => recovery.landing_zone?.image || recovery.stage?.image)
    .find(Boolean);

  let image =
    launch?.image ||
    pad?.image ||
    recoveryImage ||
    rocket?.image ||
    launchPhotoFallbacksByPad[pad?.nickname] ||
    FALCON_FALLBACK_IMAGE;

  // Don't show Falcon pad stock art for Starship missions without a real launch photo.
  if (rocket?.name === "Starship" && (!launch?.image || isFalconPadFallback(image))) {
    image = pad?.image || rocket?.image || launchPhotoFallbacksByPad[pad?.nickname] || STARSHIP_FALLBACK_IMAGE;
  }

  return resolveHomePhotoUrl(image);
};

const normalizeLaunchTime = (time) => (time ? String(time).replace(/Z$/, "") : time);

const mergeLaunchDetail = (launch, detail) => {
  if (!launch && !detail) {
    return null;
  }

  const merged = { ...(launch || {}), ...(detail || {}) };

  return {
    ...merged,
    image: detail?.image || launch?.image || merged.image,
    time: normalizeLaunchTime(detail?.time || launch?.time),
  };
};

const fetchHydratedLaunch = async (launch) => {
  if (!launch?.id) {
    return mergeLaunchDetail(launch, null);
  }

  try {
    const detail = await fetchNewLaunchInfo(launch.id);
    return mergeLaunchDetail(launch, detail);
  } catch (error) {
    return mergeLaunchDetail(launch, null);
  }
};

const getStageId = (recovery) => {
  if (recovery?.stage == null) {
    return null;
  }

  if (typeof recovery.stage === "object") {
    return recovery.stage.id ?? null;
  }

  return recovery.stage;
};

const getStageFlightLabel = (recovery) => {
  const name = recovery.stage?.name || (typeof recovery.stage === "string" ? recovery.stage : "");
  const flightNumber = Number(recovery.num_flights || recovery.stage?.num_launches);

  if (!name) {
    return "";
  }

  if (Number.isFinite(flightNumber) && flightNumber > 0 && !new RegExp(`-${flightNumber}$`).test(name)) {
    return `${name}-${flightNumber}`;
  }

  return name;
};

const getStageEntries = (recoveries, stagesString = "") => {
  const fromRecoveries = (recoveries || [])
    .map((recovery) => {
      const label = getStageFlightLabel(recovery);
      if (!label) {
        return null;
      }

      return {
        id: getStageId(recovery),
        label,
      };
    })
    .filter(Boolean);

  if (fromRecoveries.length) {
    return fromRecoveries;
  }

  if (stagesString && stagesString !== "Unknown") {
    return [{ id: null, label: stagesString }];
  }

  return [{ id: null, label: "Unknown" }];
};

const StageLinks = ({ entries, className = "bt-stage-links" }) => (
  <span className={className}>
    {entries.map((entry, index) => (
      <React.Fragment key={`${entry.id || entry.label}-${index}`}>
        {index > 0 ? ", " : null}
        {entry.id ? (
          <Link
            to={`/stage/${entry.id}/`}
            className="bt-stage-link"
            onClick={(event) => event.stopPropagation()}
          >
            {entry.label}
          </Link>
        ) : (
          entry.label
        )}
      </React.Fragment>
    ))}
  </span>
);

const getLaunchDossier = (launch, rockets, pads, zones) => {
  const rocket = rockets.find((item) => item.id === launch.rocket)?.name || "Unknown";
  const pad = pads.find((item) => item.id === launch.pad)?.nickname || "Unknown";
  const recoveries = launch.stage_and_recoveries?.length ? launch.stage_and_recoveries : launch.recoveries || [];
  const hasKnownRecovery = recoveries.some((recovery) => {
    const method = String(recovery.method || "").toUpperCase();
    const methodSuccess = String(recovery.method_success || "").toUpperCase();
    return recovery.landing_zone || (method && method !== "TBD") || (methodSuccess && methodSuccess !== "TBD");
  });
  let landing =
    recoveries.length && hasKnownRecovery
      ? (makeRecoveryString(recoveries, zones) || "TBD").replace(/Tbd/g, "TBD")
      : "TBD";
  landing = landing.replace(/undefined/g, "").replace(/\s+/g, " ").trim();
  if (/^\(.+\)$/.test(landing)) {
    landing = landing.slice(1, -1);
  }
  if (!landing) {
    landing = "TBD";
  }
  const outcome = toTitleCase(launch.launch_outcome || "TBD").replace("Tbd", "TBD");
  const stageEntries = getStageEntries(recoveries, launch.stages_string);
  const stages = stageEntries.map((entry) => entry.label).join(", ");

  return {
    rocket,
    pad,
    landing,
    outcome,
    stage: stages || "Unknown",
    stageEntries,
    time: formatTime(launch.time),
    elapsed: getElapsedLabel(launch.time),
  };
};

const fallbackYears = ["2017", "2018", "2019", "2020", "2021", "2022", "2023", "2024", "2025", "2026"];
const fallbackLaunchCounts = [18, 21, 13, 26, 31, 61, 98, 134, 138, 104];
const fallbackLaunchesPerYear = Object.fromEntries(fallbackYears.map((year, index) => [year, fallbackLaunchCounts[index]]));
const fallbackSeries = [
  {
    data: fallbackLaunchCounts,
    label: "Falcon 9",
    area: false,
    stack: "total",
    curve: "linear",
    color: launchPalette[0],
  },
];
const fallbackHeatmap = {
  max: 12,
  years: [
    { year: 2022, months: [4, 3, 4, 5, 4, 7, 5, 7, 6, 6, 7, 8] },
    { year: 2023, months: [7, 6, 8, 8, 9, 9, 10, 9, 10, 10, 11, 10] },
    { year: 2024, months: [10, 9, 11, 10, 11, 12, 11, 12, 12, 11, 12, 12] },
    { year: 2025, months: [11, 10, 12, 11, 12, 12, 11, 12, 12, 12, 11, 12] },
    { year: 2026, months: [8, 9, 9, 10, 8, 0, 0, 0, 0, 0, 0, 0] },
  ],
};
const fallbackBoosters = [
  { id: "B1060", name: "B1060", num_launches: 19, status: "RETIRED" },
  { id: "B1062", name: "B1062", num_launches: 18, status: "ACTIVE" },
  { id: "B1061", name: "B1061", num_launches: 17, status: "RETIRED" },
  { id: "B1058", name: "B1058", num_launches: 16, status: "RETIRED" },
  { id: "B1067", name: "B1067", num_launches: 16, status: "ACTIVE" },
  { id: "B1071", name: "B1071", num_launches: 16, status: "ACTIVE" },
  { id: "B1069", name: "B1069", num_launches: 15, status: "ACTIVE" },
  { id: "B1077", name: "B1077", num_launches: 15, status: "ACTIVE" },
  { id: "B1073", name: "B1073", num_launches: 14, status: "ACTIVE" },
  { id: "B1080", name: "B1080", num_launches: 10, status: "ACTIVE" },
];
const fallbackStarshipBoosters = [
  { id: "Ship 33", name: "Ship 33", num_launches: 0, status: "ACTIVE" },
  { id: "Booster 14", name: "Booster 14", num_launches: 1, status: "ACTIVE" },
  { id: "Ship 30", name: "Ship 30", num_launches: 1, status: "RETIRED" },
  { id: "B7", name: "B7", num_launches: 1, status: "EXPENDED" },
];
const fallbackDragonSpacecraft = [
  { id: "C212", name: "C212", status: "ACTIVE" },
  { id: "C211", name: "C211", status: "ACTIVE" },
  { id: "C210", name: "C210", status: "ACTIVE" },
  { id: "C209", name: "C209", status: "ACTIVE" },
  { id: "C208", name: "C208", status: "ACTIVE" },
  { id: "C207", name: "C207", status: "ACTIVE" },
  { id: "C206", name: "C206", status: "ACTIVE" },
];
const rankingFallbacks = {
  falcon: fallbackBoosters,
  starship: fallbackStarshipBoosters,
};
const isRankingBooster = (stage, family) => {
  if (family === "starship") {
    return !/^OV-/i.test(stage.name || "");
  }

  if (family === "falcon") {
    return [5, 6].includes(Number(stage.rocket));
  }

  return true;
};
const buildRankingView = (stages, family) => {
  const rankingFallback = rankingFallbacks[family] || fallbackBoosters;
  const rankingBoosters = (stages || [])
    .filter((stage) => isRankingBooster(stage, family))
    .filter((stage) => Number(stage.num_launches) > 0)
    .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0));
  const sourceBoosters = rankingBoosters.length ? rankingBoosters : rankingFallback;

  return {
    boosters: sourceBoosters.slice(0, 10),
  };
};
const getActiveCount = (items) => (items || []).filter((item) => statusIsActive(item.status)).length;
const isStarshipShip = (stage) => /^Ship\b/i.test(stage.name || "");
const buildFalconSnapshot = (falconStages, dragonSpacecraft) => ({
  note: "Active vehicles only",
  items: [
    {
      label: "Active Falcons",
      value: getActiveCount((falconStages || []).filter((stage) => isRankingBooster(stage, "falcon"))),
    },
    {
      label: "Active Dragons",
      value: getActiveCount(dragonSpacecraft || fallbackDragonSpacecraft),
    },
  ],
});
const buildStarshipSnapshot = (starshipStages) => {
  const validStages = (starshipStages || []).filter((stage) => isRankingBooster(stage, "starship"));

  return {
    note: "Active vehicles only",
    items: [
      {
        label: "Active Boosters",
        value: getActiveCount(validStages.filter((stage) => !isStarshipShip(stage))),
      },
      {
        label: "Active Ships",
        value: getActiveCount(validStages.filter(isStarshipShip)),
      },
    ],
  };
};
const fallbackRankingCache = {
  falcon: {
    ...buildRankingView(fallbackBoosters, "falcon"),
    snapshot: buildFalconSnapshot(fallbackBoosters, fallbackDragonSpacecraft),
  },
  starship: {
    ...buildRankingView(fallbackStarshipBoosters, "starship"),
    snapshot: buildStarshipSnapshot(fallbackStarshipBoosters),
  },
};
const fallbackReuseTimeline = addSyntheticReuseData([
  { id: "B1062", name: "B1062", active: true, count: 18, dots: [0, 8, 18, 22, 32, 38, 42, 48, 52, 58, 66, 72, 78, 86] },
  { id: "B1067", name: "B1067", active: true, count: 16, dots: [0, 6, 12, 18, 25, 32, 36, 42, 48] },
  { id: "B1069", name: "B1069", active: true, count: 15, dots: [0, 8, 14, 20, 27, 34, 38, 44, 50, 56] },
  { id: "B1071", name: "B1071", active: true, count: 16, dots: [0, 6, 10, 18, 23, 29, 34, 40, 46, 52, 58] },
  { id: "B1073", name: "B1073", active: true, count: 14, dots: [0, 6, 9, 16, 20, 25, 31, 39, 46] },
  { id: "B1077", name: "B1077", active: true, count: 15, dots: [0, 6, 10, 16, 22, 30, 36, 42, 48, 52] },
  { id: "B1058", name: "B1058", active: false, count: 9, dots: [0, 10, 16, 25, 32, 40, 49, 58, 63, 70] },
  { id: "B1061", name: "B1061", active: false, count: 17, dots: [0, 8, 14, 24, 38, 48, 58, 65, 74] },
]);

const Home = () => {
  const currentYear = new Date().getUTCFullYear();
  const [rockets, setRockets] = useState([]);
  const [pads, setPads] = useState([]);
  const [zones, setZones] = useState([]);
  const [launchesPerVehicle, setLaunchesPerVehicle] = useState(fallbackSeries);
  const [launchesPerYear, setLaunchesPerYear] = useState(fallbackLaunchesPerYear);
  const [launchChartReady, setLaunchChartReady] = useState(false);
  const [launchChartWidth, setLaunchChartWidth] = useState(null);
  const [stats, setStats] = useState({
    numMissions: 0,
    numSuccesses: 0,
    numLandings: 0,
    shortestTimeBetweenFlights: 0,
    numStageReflights: 0,
  });
  const [lastLaunch, setLastLaunch] = useState(null);
  const [nextLaunch, setNextLaunch] = useState(null);
  const [clockNow, setClockNow] = useState(Date.now());
  const [reuseTimeline, setReuseTimeline] = useState(fallbackReuseTimeline);
  const [reuseView, setReuseView] = useState("relative");
  const [rankingFamily, setRankingFamily] = useState("falcon");
  const [rankingCache, setRankingCache] = useState(fallbackRankingCache);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [showRankingLoading, setShowRankingLoading] = useState(false);
  const [cadenceHeatmap, setCadenceHeatmap] = useState(fallbackHeatmap);
  const [recentLaunches, setRecentLaunches] = useState([]);
  const [recentLaunchesLoading, setRecentLaunchesLoading] = useState(true);

  const [totalLaunchesCurrentYear, setTotalLaunchesCurrentYear] = useState(0);

  const launchChartRef = useRef(null);
  const [dataLoaded, setDataLoaded] = useState(false); // Ref to signify when data has been loaded
  const reuseChart = useMemo(() => buildReuseChart(reuseTimeline, reuseView), [reuseTimeline, reuseView]);
  const hasUpcomingLaunch =
    nextLaunch && !Number.isNaN(parseUtcMs(nextLaunch.time)) && parseUtcMs(nextLaunch.time) > clockNow;
  const clockLaunch = hasUpcomingLaunch ? nextLaunch : lastLaunch || nextLaunch;
  const clockLaunchDossier = useMemo(
    () => (clockLaunch ? getLaunchDossier(clockLaunch, rockets, pads, zones) : null),
    [clockLaunch, pads, rockets, zones]
  );
  const clockParts = useMemo(
    () => (clockLaunch ? getClockParts(clockLaunch.time, clockNow) : null),
    [clockLaunch, clockNow]
  );
  const activeRanking = rankingCache[rankingFamily] || fallbackRankingCache[rankingFamily];
  const mostFlownBoosters = activeRanking.boosters;
  const fleetSnapshot = activeRanking.snapshot;

  useEffect(() => {
    (async () => {
      const [padData, zoneData, rocketData] = await Promise.all([
        fetchPads("", {}),
        fetchZones("", {}),
        fetchRockets(),
      ]);

      setPads(padData);
      setZones(zoneData);
      setRockets(rocketData);
      setDataLoaded(true);
    })();
  }, []);

  useEffect(() => {
    const fetchVisualData = async () => {
      setRankingLoading(true);
      try {
        const [falconStageData, starshipStageData, dragonSpacecraftData, familyData] = await Promise.all([
          fetchStages("", null, "falcon", "BOOSTER"),
          fetchStages("", null, "starship", "BOOSTER"),
          fetchSpacecraft("", null, "dragon"),
          fetchRocketFamilies(),
        ]);

        setRankingCache({
          falcon: {
            ...buildRankingView(falconStageData.stages, "falcon"),
            snapshot: buildFalconSnapshot(falconStageData.stages, dragonSpacecraftData.spacecraft),
          },
          starship: {
            ...buildRankingView(starshipStageData.stages, "starship"),
            snapshot: buildStarshipSnapshot(starshipStageData.stages),
          },
        });
        setRankingLoading(false);

        const falconBoosters = (falconStageData.stages || [])
          .filter((stage) => Number(stage.num_launches) > 0)
          .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0));
        const timelineBoosters = falconBoosters.length ? falconBoosters.slice(0, 8) : fallbackBoosters.slice(0, 8);

        const timelineDetails = await Promise.all(
          timelineBoosters.map(async (stage) => {
            try {
              const detail = await fetchStageInfo(stage.id);
              const flights = extractFlightDates(detail.stage_and_recoveries);

              if (!flights.length) {
                return null;
              }

              return buildTimelineRowFromFlights({
                id: stage.id,
                name: stage.name,
                active: statusIsActive(stage.status),
                flights,
                count: Number(stage.num_launches || flights.length),
              });
            } catch (error) {
              return null;
            }
          })
        );

        const timelineRows = timelineDetails.filter(Boolean);
        setReuseTimeline(timelineRows.length ? timelineRows : fallbackReuseTimeline);

        const calendarData = await getCalendarInformation(buildCalendarFilter(familyData));
        const launches = changeTimeToLocal(calendarData.launches || []);
        const nowYear = new Date().getUTCFullYear();
        const years = Array.from({ length: 5 }, (_, index) => nowYear - 4 + index);
        const buckets = years.map((year) => ({ year, months: Array(12).fill(0) }));

        launches.forEach((launch) => {
          const date = new Date(launch.time);
          const bucket = buckets.find((item) => item.year === date.getUTCFullYear());
          if (bucket) {
            bucket.months[date.getUTCMonth()] += 1;
          }
        });

        const hasCadenceData = buckets.some((item) => item.months.some((count) => count > 0));
        setCadenceHeatmap(
          hasCadenceData
            ? {
                years: buckets,
                max: Math.max(1, ...buckets.flatMap((item) => item.months)),
              }
            : fallbackHeatmap
        );
      } catch (error) {
        setRankingCache(fallbackRankingCache);
        setRankingLoading(false);
        setReuseTimeline(fallbackReuseTimeline);
        setCadenceHeatmap(fallbackHeatmap);
      }
    };

    fetchVisualData();
  }, []);

  useEffect(() => {
    if (!rankingLoading) {
      setShowRankingLoading(false);
      return undefined;
    }

    const loadingTimer = setTimeout(() => setShowRankingLoading(true), 350);
    return () => clearTimeout(loadingTimer);
  }, [rankingLoading]);

  useLayoutEffect(() => {
    const chartElement = launchChartRef.current;

    if (!chartElement) {
      return undefined;
    }

    const updateChartWidth = () => {
      setLaunchChartWidth(Math.max(320, Math.min(chartElement.offsetWidth || 0, 1100)));
    };

    updateChartWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateChartWidth);
      return () => window.removeEventListener("resize", updateChartWidth);
    }

    const observer = new ResizeObserver(updateChartWidth);
    observer.observe(chartElement);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!dataLoaded) {
      return;
    }

    const fetchRecentLaunches = async () => {
      setRecentLaunchesLoading(true);
      try {
        const familyData = await fetchRocketFamilies();
        const calendarData = await getCalendarInformation(buildCalendarFilter(familyData));
        const now = Date.now();
        const completedLaunches = changeTimeToLocal(calendarData.launches || [])
          .filter((launch) => {
            const launchTime = parseUtcMs(launch.time);
            const outcome = String(launch.launch_outcome || "").toUpperCase();
            return !Number.isNaN(launchTime) && launchTime <= now && outcome !== "TBD";
          })
          .sort((a, b) => parseUtcMs(b.time) - parseUtcMs(a.time))
          .slice(0, 12);

        const detailedLaunches = await Promise.all(
          completedLaunches.slice(0, 3).map(async (launch) => {
            return fetchHydratedLaunch(launch);
          })
        );

        setRecentLaunches([...detailedLaunches, ...completedLaunches.slice(3)]);
      } catch (error) {
        setRecentLaunches([]);
      } finally {
        setRecentLaunchesLoading(false);
      }
    };

    fetchRecentLaunches();
  }, [dataLoaded]);

  useEffect(() => {
    if (!dataLoaded) {
      return;
    }
    const fetchData = async () => {
      let homeData;
      try {
        [homeData] = await Promise.all([fetchHomeInfo(new Date(currentYear - 2, 0, 1), "exponential")]);
      } catch (error) {
        setLaunchesPerVehicle(fallbackSeries);
        setLaunchesPerYear(fallbackLaunchesPerYear);
        setLaunchChartReady(true);
        setTotalLaunchesCurrentYear(fallbackLaunchesPerYear[currentYear] || 0);
        setStats({
          numMissions: fallbackLaunchCounts.reduce((total, count) => total + count, 0),
          numSuccesses: 0,
          numLandings: 0,
          shortestTimeBetweenFlights: 0,
          numStageReflights: fallbackBoosters.reduce((total, booster) => total + Number(booster.num_launches || 0), 0),
        });
        return;
      }

      // Generate launches per vehicle per year graph:
      let seriesInputData = [];
      let totalLaunchesPerYear = [];
      const years = homeData["years"];
      for (const [key, value] of Object.entries(homeData["launches_per_rocket_per_year"])) {
        const launches = JSON.parse(value.replace(/'/g, '"'));

        if (totalLaunchesPerYear.length === 0) {
          totalLaunchesPerYear = [...launches];
        } else {
          totalLaunchesPerYear = totalLaunchesPerYear.map((launch, index) => launch + launches[index]);
        }

        const rocket = rockets.find((rocket) => String(rocket.id) === key);
        seriesInputData.push({
          data: launches,
          label: rocket?.name || `Rocket ${key}`,
          area: false,
          stack: "total",
          curve: "linear",
          color: getLaunchChartColor(rocket, seriesInputData.length),
        });
      }

      const launchesPerYear = {};
      for (const [index, value] of Object.entries(totalLaunchesPerYear)) {
        launchesPerYear[years[index]] = value;
      }

      setLaunchesPerVehicle(seriesInputData);
      setLaunchesPerYear(launchesPerYear);
      setLaunchChartReady(true);

      // Set the new data fields
      setTotalLaunchesCurrentYear(homeData["total_launches_current_year"]);
      setStats({
        numMissions: homeData["num_missions"],
        numSuccesses: homeData["num_successes"],
        numLandings: homeData["num_landings"],
        shortestTimeBetweenFlights: homeData["shortest_time_between_launches"],
        numStageReflights: homeData["num_stage_reflights"],
      });
      const [hydratedNextLaunch, hydratedLastLaunch] = await Promise.all([
        fetchHydratedLaunch(homeData.next_launch),
        fetchHydratedLaunch(homeData.last_launch),
      ]);

      setNextLaunch(hydratedNextLaunch);
      setLastLaunch(hydratedLastLaunch);
    };
    fetchData();
  }, [dataLoaded, rockets, currentYear]);

  useEffect(() => {
    const interval = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const getChartWidth = () => launchChartWidth || 620;
  const clockIsUpcoming = Boolean(hasUpcomingLaunch);

  return (
    <div className="graph-container bt-home">
      <section className="bt-home-overview">
        <div className="bt-panel bt-launches-panel">
          <div className="bt-panel-head">
            <div>
              <p className="bt-section-label">Launch cadence</p>
              <h1 className="bt-panel-title">Launches per year</h1>
            </div>
            <span className="bt-panel-note">Falcon, Dragon, and Starship combined</span>
          </div>
          <div className="chart-and-stats" ref={launchChartRef}>
            {launchChartReady && launchChartWidth ? (
              <DarkBarChart
                seriesInput={launchesPerVehicle}
                xAxisInput={[{ data: Object.keys(launchesPerYear), type: "linear", scaleType: "band" }]}
                showLegend={true}
                chartWidth={getChartWidth()}
                totalLaunches={launchesPerYear}
              />
            ) : (
              <div className="bt-chart-loading" aria-label="Loading launch cadence chart"></div>
            )}
          </div>
        </div>

        <div
          className={`bt-panel bt-ranking-panel ${rankingFamily === "starship" ? "starship-rank" : "falcon-rank"}`}
          aria-busy={rankingLoading}
        >
          <div className="bt-panel-head">
            <div>
              <p className="bt-section-label">Most flown</p>
              <h2 className="bt-panel-title">Booster ranking</h2>
            </div>
            <div className={`bt-view-toggle ${rankingFamily === "starship" ? "starship-active" : ""}`} aria-label="Booster ranking family">
              {[
                ["falcon", "Falcon"],
                ["starship", "Starship"],
              ].map(([key, label]) => (
                <button
                  className={rankingFamily === key ? "active" : ""}
                  key={key}
                  type="button"
                  onClick={() => setRankingFamily(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {showRankingLoading ? (
            <div className="bt-rank-loading" role="status">
              <span>Loading fleet data</span>
              {Array.from({ length: 6 }).map((_, index) => (
                <i key={index}></i>
              ))}
            </div>
          ) : (
            <>
              <div className="bt-rank-list">
                {mostFlownBoosters.map((booster, index) => {
                  const maxFlights = Math.max(...mostFlownBoosters.map((item) => Number(item.num_launches || 0)), 1);
                  const isActive = statusIsActive(booster.status);
                  return (
                    <div className="bt-rank-row" key={booster.id}>
                      <span>{index + 1}</span>
                      <Link to={`/stage/${booster.id}/`} className="bt-rank-name">
                        {booster.name}
                      </Link>
                      <span className="bt-rank-track">
                        <span
                          className="bt-rank-fill"
                          style={{ "--rank-width": `${(Number(booster.num_launches || 0) / maxFlights) * 100}%` }}
                        ></span>
                      </span>
                      <span>{booster.num_launches}</span>
                      <i
                        className={isActive ? "live" : "dead"}
                        title={isActive ? "Active" : "Inactive"}
                        aria-label={isActive ? "Active" : "Inactive"}
                      ></i>
                    </div>
                  );
                })}
              </div>
              <div className="bt-fleet-snapshot">
                <div className="bt-fleet-head">
                  <p className="bt-section-label">Fleet snapshot</p>
                  <span>{fleetSnapshot.note}</span>
                </div>
                <div className="bt-fleet-grid">
                  {fleetSnapshot.items.map((item) => (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="bt-panel bt-mission-pulse">
        <div className="bt-panel-head">
          <div>
            <p className="bt-section-label">Launches</p>
            <h2 className="bt-panel-title">Upcoming and past launches</h2>
          </div>
          <Link to="/launches/" className="bt-panel-action">
            View all launches
          </Link>
        </div>

        <div className="bt-pulse-body">
          {clockLaunch ? (
            <div className="bt-pulse-countdown">
              <Link
                to={`/launch/${clockLaunch.id}/`}
                className="bt-pulse-image"
                style={{
                  backgroundImage: `url(${getLaunchPhotoUrl(clockLaunch, pads, rockets)})`,
                }}
                aria-label={`View ${clockLaunch.name} launch details`}
              />
              <Link to={`/launch/${clockLaunch.id}/`} className="bt-pulse-copy">
                <span className="bt-section-label">{clockIsUpcoming ? "Next launch" : "Last launch"}</span>
                <strong>{clockLaunch.name}</strong>
                <span>{clockLaunchDossier?.time}</span>
              </Link>
              <Link to={`/launch/${clockLaunch.id}/`} className="bt-pulse-clock">
                <span>{clockParts?.prefix}</span>
                <strong>
                  {clockParts?.days}
                  <small>d</small> {String(clockParts?.hours).padStart(2, "0")}
                  <small>h</small> {String(clockParts?.minutes).padStart(2, "0")}
                  <small>m</small> {String(clockParts?.seconds).padStart(2, "0")}
                  <small>s</small>
                </strong>
              </Link>
              <span className="bt-pulse-details">
                <span>
                  <small>Rocket</small>
                  {clockLaunchDossier?.rocket}
                </span>
                <span>
                  <small>Pad</small>
                  {clockLaunchDossier?.pad}
                </span>
                <span>
                  <small>Stage</small>
                  <StageLinks entries={clockLaunchDossier?.stageEntries || [{ id: null, label: "Unknown" }]} />
                </span>
                <span>
                  <small>{clockIsUpcoming ? "Recovery" : "Landing"}</small>
                  {clockLaunchDossier?.landing}
                </span>
              </span>
              <Link to={`/launch/${clockLaunch.id}/`} className="bt-pulse-detail-action">
                View launch details
              </Link>
            </div>
          ) : (
            <div className="bt-pulse-empty">
              <p className="bt-section-label">Next launch</p>
              <strong>Launch data unavailable</strong>
            </div>
          )}

          <div className="bt-pulse-recent" aria-busy={recentLaunchesLoading}>
            <div className="bt-pulse-recent-head">
              <p className="bt-section-label">Previous launches</p>
              <span>{recentLaunchesLoading ? "Loading" : `${Math.min(recentLaunches.length, 3)} shown`}</span>
            </div>
            <div className="bt-pulse-card-list">
              {recentLaunchesLoading ? (
                Array.from({ length: 3 }).map((_, index) => <span className="bt-pulse-loading" key={index}></span>)
              ) : recentLaunches.length ? (
                recentLaunches.slice(0, 3).map((launch) => {
                  const dossier = getLaunchDossier(launch, rockets, pads, zones);
                  const outcome = dossier.outcome || "TBD";
                  const outcomeClass = outcome.toLowerCase().includes("success")
                    ? "success"
                    : outcome.toLowerCase().includes("partial")
                      ? "partial"
                      : outcome.toLowerCase().includes("fail")
                        ? "failure"
                        : "";

                  return (
                    <div className="bt-pulse-card" key={launch.id}>
                      <Link
                        to={`/launch/${launch.id}/`}
                        className="bt-pulse-card-hit"
                        aria-label={`View ${launch.name} launch details`}
                      />
                      <span
                        className="bt-pulse-card-image"
                        style={{
                          backgroundImage: `url(${getLaunchPhotoUrl(launch, pads, rockets)})`,
                        }}
                      ></span>
                      <span className="bt-pulse-card-main">
                        <span className={`bt-outcome-badge ${outcomeClass}`}>{outcome}</span>
                        <span>{getShortDate(launch.time)}</span>
                      </span>
                      <span className="bt-pulse-card-info">
                        <strong>{launch.name}</strong>
                        <span className="bt-pulse-card-meta">
                          <span>{dossier.rocket}</span>
                          <StageLinks entries={dossier.stageEntries} />
                          <span>{dossier.pad}</span>
                        </span>
                      </span>
                      <span className="bt-pulse-card-arrow" aria-hidden="true">
                        &gt;
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="bt-pulse-empty compact">
                  <strong>No recent launch data</strong>
                </div>
              )}
            </div>
          </div>
        </div>

      </section>

      <section className="bt-home-stats">
        <StatCard title={"Missions"} value={stats["numMissions"]} />
        <StatCard title={"Landings"} value={stats["numLandings"]} />
        <StatCard title={"Stage Reuses"} value={stats["numStageReflights"]} />
        <StatCard title={`Estimated ${currentYear}`} value={totalLaunchesCurrentYear} />
      </section>

      <section className="bt-panel bt-reuse-panel">
        <div className="bt-panel-head">
          <div>
            <p className="bt-section-label">Booster reuse timeline</p>
            <h2 className="bt-panel-title">Flights since debut</h2>
            <p className="bt-panel-subnote">{reuseViewLabels[reuseView].helper}</p>
          </div>
          <div className="bt-reuse-tools">
            <div className="bt-mini-legend">
              <span>Active</span>
              <span className="retired">Retired</span>
            </div>
            <div className="bt-view-toggle" aria-label="Reuse timeline view">
              {Object.entries(reuseViewLabels).map(([key, value]) => (
                <button
                  className={reuseView === key ? "active" : ""}
                  key={key}
                  type="button"
                  onClick={() => setReuseView(key)}
                >
                  {value.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="bt-reuse-chart">
          <div className="bt-reuse-inner">
            {reuseChart.rows.map((booster) => (
              <div
                className={`bt-reuse-row ${booster.active ? "active" : "retired"}`}
                style={{ "--last-x": booster.dots[booster.dots.length - 1]?.x || 0 }}
                key={booster.id}
              >
                <span className="bt-reuse-name">{booster.name}</span>
                <span className="bt-reuse-lane"></span>
                {booster.dots.map((dot, index) => (
                  <i
                    className="bt-reuse-dot"
                    style={{ "--x": dot.x }}
                    title={dot.title}
                    aria-label={dot.title}
                    key={`${booster.id}-${index}`}
                  ></i>
                ))}
                <span className="bt-reuse-count" style={{ "--x": booster.dots[booster.dots.length - 1]?.x || 0 }}>
                  x{booster.count}
                </span>
              </div>
            ))}
            <div className="bt-reuse-axis">
              {reuseChart.axisLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bt-home-grid">
        <div className="bt-panel bt-cadence-panel">
          <div className="bt-panel-head">
            <div>
              <p className="bt-section-label">Launch cadence</p>
              <h2 className="bt-panel-title">Monthly launch cadence</h2>
            </div>
          </div>
          <div className="bt-heatmap" aria-label="Launch cadence heatmap">
            <span className="bt-heatmap-heading">Year</span>
            {months.map((month, index) => (
              <span className="bt-heatmap-month" key={`${month}-${index}`}>
                {month}
              </span>
            ))}
            <span className="bt-heatmap-heading">Total</span>
            {cadenceHeatmap.years.map(({ year, months: yearMonths }) => (
              <React.Fragment key={year}>
                <span className="bt-heatmap-year">{year}</span>
                {yearMonths.map((count, index) => (
                  <span
                    className="bt-heatmap-cell"
                    key={`${year}-${index}`}
                    title={`${year} ${months[index]}: ${count} launches`}
                    style={{ "--intensity": 0.12 + (count / cadenceHeatmap.max) * 0.78 }}
                  >
                    {count}
                  </span>
                ))}
                <strong className="bt-heatmap-total">{yearMonths.reduce((total, count) => total + Number(count || 0), 0)}</strong>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

        </div>
  );
};

export default Home;
